import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useCompanyLogo } from '../hooks/useCompanyLogo';

const FALLBACK_GOOGLE = 'https://g.page/r/CZEPwKc5IfJFEBM/review';

type LandingData = {
  googleUrl: string;
  title: string;
  message: string;
  button: string;
  business: string;
  whatsapp: string;
  phone: string;
};

const DEFAULTS: LandingData = {
  googleUrl: FALLBACK_GOOGLE,
  title: '¡Gracias por elegirnos!',
  message: 'Tu opinión vale muchísimo para nosotros. Si quedaste conforme con nuestro trabajo, dejanos una reseña en Google — tarda menos de un minuto y nos ayuda a seguir creciendo.',
  button: 'Dejar mi reseña',
  business: 'ROCAM Neumáticos',
  whatsapp: '5493476249820',
  phone: '+5493476249820'
};

// Landing pública /r/:token — diseño racing/wireframe de rocam.com.ar
// (página diseñada por Rodrigo). El click se registra al tocar el CTA.
const ReviewRedirect: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  // Logos configurados en el sistema: normal (fondos claros) e inverso (oscuros)
  const { getLogoUrl, getLogoDarkUrl } = useCompanyLogo();
  const [data, setData] = useState<LandingData>(DEFAULTS);
  // idle: invitación · waiting: puntos titilando · confirmed: código de descuento
  const [phase, setPhase] = useState<'idle' | 'waiting' | 'confirmed'>('idle');
  const [reward, setReward] = useState<{ code: string | null; value: string | null; expiresAt: string | null; conditions?: string | null }>({ code: null, value: null, expiresAt: null, conditions: null });
  const [slowNote, setSlowNote] = useState(false);

  useEffect(() => {
    document.title = 'ROCAM — Dejá tu reseña';
    // Tipografías del sitio (Inter / JetBrains Mono / Rajdhani)
    if (!document.getElementById('rv-fonts')) {
      const link = document.createElement('link');
      link.id = 'rv-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@600;700&display=swap';
      document.head.appendChild(link);
    }
    axios.get(`/api/public/reviews/landing/${token || ''}`)
      .then((res) => setData({ ...DEFAULTS, ...(res.data || {}) }))
      .catch(() => setData(DEFAULTS));
    // Si ya reseñó y vuelve a abrir el link, mostrar directo su cupón
    axios.get(`/api/public/reviews/status/${token || ''}`)
      .then((res) => {
        if (res.data?.reviewed) {
          setReward({ code: res.data.code, value: res.data.value, expiresAt: res.data.expiresAt, conditions: res.data.conditions });
          setPhase('confirmed');
        }
      })
      .catch(() => {});
  }, [token]);

  // En modo espera: consultar cada 6s si la reseña se confirmó
  useEffect(() => {
    if (phase !== 'waiting') return;
    const check = async () => {
      try {
        const res = await axios.get(`/api/public/reviews/status/${token || ''}`);
        if (res.data?.reviewed) {
          setReward({ code: res.data.code, value: res.data.value, expiresAt: res.data.expiresAt, conditions: res.data.conditions });
          setPhase('confirmed');
        }
      } catch (_) { /* seguir esperando */ }
    };
    check();
    const interval = window.setInterval(check, 6000);
    const slow = window.setTimeout(() => setSlowNote(true), 120000);
    return () => { window.clearInterval(interval); window.clearTimeout(slow); };
  }, [phase, token]);

  const trackClick = () => {
    axios.post(`/api/public/reviews/click/${token || ''}`).catch(() => {});
    setPhase('waiting');
  };

  // Descarga el cupón como PNG estilo documento claro (diseño de Rodrigo v2):
  // fondo blanco con wireframe, caja negra inclinada con el logo real,
  // código gigante y clasificación tipo ficha técnica
  // Dibuja el cupón (mismo diseño de siempre) y devuelve el PNG como dataURL.
  const buildCouponDataUrl = (): Promise<string> => new Promise((resolve, reject) => {
    const code = reward.code || 'ROCAM';
    const descU = `${(reward.value || '').toUpperCase()} DE DESCUENTO`;
    const vigU = reward.expiresAt ? `VÁLIDO HASTA ${new Date(reward.expiresAt).toLocaleDateString('es-AR')}` : '';
    const condU = (reward.conditions || '').toUpperCase();
    const negocio = data.business || 'ROCAM Neumáticos';
    // Logo configurado en el sistema (versión normal, para el fondo claro del PNG)
    const logoImg = new Image();
    logoImg.src = getLogoUrl('/rocam-new-logo-2025.png');

    const draw = () => {
      try {
      const W = 1000, H = 620, s = 2;
      const c = document.createElement('canvas');
      c.width = W * s; c.height = H * s;
      const x = c.getContext('2d') as any;
      if (!x) return;
      x.scale(s, s);

      // fondo blanco + wireframe ondulado
      x.fillStyle = '#ffffff'; x.fillRect(0, 0, W, H);
      x.strokeStyle = 'rgba(0,0,0,0.09)'; x.lineWidth = 0.7;
      let i, j;
      for (j = 0; j <= H + 56; j += 56) {
        x.beginPath(); x.moveTo(-20, j);
        for (i = -20; i < W + 40; i += 70) {
          x.quadraticCurveTo(i + 17, j - 14 + ((i / 70) % 2) * 28, i + 35, j);
          x.quadraticCurveTo(i + 52, j + 14 - ((i / 70) % 2) * 28, i + 70, j);
        }
        x.stroke();
      }
      for (i = 0; i <= W + 56; i += 56) {
        x.beginPath(); x.moveTo(i, -20);
        for (j = -20; j < H + 40; j += 70) {
          x.quadraticCurveTo(i - 14 + ((j / 70) % 2) * 28, j + 17, i, j + 35);
          x.quadraticCurveTo(i + 14 - ((j / 70) % 2) * 28, j + 52, i, j + 70);
        }
        x.stroke();
      }
      x.fillStyle = 'rgba(255,255,255,0.55)'; x.fillRect(0, 0, W, H);

      const L = 64, R = W - 64;
      const ink = '#141414', ink2 = '#565656', ink3 = '#8c8c8c', red = '#d92038';

      // fila superior: tag izq + marca der
      x.strokeStyle = ink3; x.lineWidth = 1.2;
      x.strokeRect(L, 52, 11, 11);
      x.textAlign = 'left'; x.fillStyle = ink3;
      x.font = "500 12px 'JetBrains Mono', monospace"; x.letterSpacing = '3px';
      x.fillText('CUP-' + new Date().getFullYear() + '-CLIENTES', L + 24, 63);
      x.textAlign = 'right';
      x.fillText('ROCAM // CUPÓN', R, 63);
      x.letterSpacing = '0px';

      // cabecera: logo de marca (el paralelogramo rojo ya trae la inclinación)
      const ly = 118, lh = 46;
      let lwBox = 150;
      if (logoImg && logoImg.naturalWidth) {
        const ilw = lh * (logoImg.naturalWidth / logoImg.naturalHeight);
        x.drawImage(logoImg, L, ly, ilw, lh);
        lwBox = ilw;
      }
      // bloque sector
      x.textAlign = 'left';
      x.fillStyle = ink3; x.font = "500 12px 'JetBrains Mono', monospace"; x.letterSpacing = '3px';
      x.fillText('BENEFICIO', L + lwBox + 42, ly + 8);
      x.letterSpacing = '0px';
      x.fillStyle = ink; x.font = "700 24px 'Inter', sans-serif";
      x.fillText('Cupón · Clientes', L + lwBox + 42, ly + 34);
      x.fillStyle = ink3; x.font = "500 11px 'JetBrains Mono', monospace"; x.letterSpacing = '2px';
      x.fillText('DESCUENTO POR RESEÑA', L + lwBox + 42, ly + 54);
      x.letterSpacing = '0px';
      // clasificación a la derecha
      x.textAlign = 'right';
      x.fillStyle = ink3; x.font = "500 12px 'JetBrains Mono', monospace"; x.letterSpacing = '3px';
      x.fillText('// CLASIFICACIÓN', R, ly + 14);
      x.fillStyle = ink; x.font = "700 15px 'JetBrains Mono', monospace";
      x.fillText('USO CLIENTE', R, ly + 38);
      x.letterSpacing = '0px';

      // divisor fuerte
      x.strokeStyle = 'rgba(0,0,0,0.35)'; x.lineWidth = 1.5;
      x.beginPath(); x.moveTo(L, 232); x.lineTo(R, 232); x.stroke();

      // cuerpo: eyebrow + código gigante
      x.textAlign = 'left';
      x.fillStyle = red; x.font = "600 15px 'JetBrains Mono', monospace"; x.letterSpacing = '5px';
      x.fillText('// CUPÓN DE DESCUENTO', L, 312);
      x.letterSpacing = '0px';
      x.fillStyle = ink; x.font = "700 76px 'JetBrains Mono', monospace"; x.letterSpacing = '2px';
      x.fillText(code, L, 402);
      x.letterSpacing = '0px';
      // datos a la derecha del código
      x.textAlign = 'right';
      x.fillStyle = ink2; x.font = "600 15px 'JetBrains Mono', monospace"; x.letterSpacing = '2px';
      x.fillText(descU, R, 380);
      x.fillStyle = ink3; x.font = "500 13px 'JetBrains Mono', monospace";
      x.fillText(vigU, R, 402);
      x.letterSpacing = '0px';

      // divisor suave
      x.strokeStyle = 'rgba(0,0,0,0.12)'; x.lineWidth = 1;
      x.beginPath(); x.moveTo(L, 452); x.lineTo(R, 452); x.stroke();

      // nota de uso
      x.textAlign = 'left';
      x.fillStyle = ink2; x.font = "400 15px 'Inter', sans-serif";
      x.fillText('Mostrá este cupón en el mostrador para aplicar el descuento.', L, 490);
      // condiciones (alcance + tope)
      if (condU) {
        x.fillStyle = ink3; x.font = "500 12px 'JetBrains Mono', monospace"; x.letterSpacing = '1px';
        x.fillText(condU, L, 514);
        x.letterSpacing = '0px';
      }

      // pie
      x.fillStyle = ink3; x.font = "500 12px 'JetBrains Mono', monospace"; x.letterSpacing = '2px';
      x.fillText(negocio.toUpperCase(), L, H - 52);
      x.letterSpacing = '0px';
      x.fillStyle = red; x.fillRect(R - 90, H - 58, 90, 3);

      resolve(c.toDataURL('image/png'));
      } catch (e) { reject(e); }
    };

    // Esperar fuentes + logo antes de dibujar
    const start = () => {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(draw).catch(draw);
      } else {
        draw();
      }
    };
    if (logoImg.complete) start();
    else { logoImg.onload = start; logoImg.onerror = start; }
  });

  const downloadCoupon = () => {
    buildCouponDataUrl().then((url) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = `cupon-rocam-${reward.code || 'ROCAM'}.png`;
      a.click();
    }).catch(() => {});
  };

  return (
    <div className="rv-page">
      <style>{`
        .rv-page{
          --bg:#0a0a0a; --bg-card:#141414; --bg-card-2:#181818;
          --fg:#f5f5f5; --fg-2:#9a9a9a; --fg-3:#5e5e5e;
          --line:rgba(255,255,255,0.08); --line-2:rgba(255,255,255,0.16); --line-3:rgba(255,255,255,0.28);
          --gold:#f5b301;
          background:var(--bg); color:var(--fg);
          font-family:'Inter',system-ui,sans-serif; font-size:16px; line-height:1.6;
          -webkit-font-smoothing:antialiased;
          min-height:100dvh; display:flex; flex-direction:column;
          align-items:center; justify-content:space-between;
          padding:clamp(28px,6vh,64px) 20px clamp(20px,4vh,36px);
          position:relative; overflow-x:hidden;
        }
        .rv-page a{ color:inherit; text-decoration:none; }
        .rv-page img{ display:block; max-width:100%; }
        .rv-wf{
          position:fixed; inset:0; z-index:0; pointer-events:none;
          background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280' viewBox='0 0 280 280'><g stroke='white' stroke-width='0.6' fill='none' opacity='0.7'><path d='M0,40 C70,10 100,55 140,38 C180,21 220,60 280,40'/><path d='M0,92 C60,120 110,75 140,98 C175,124 230,80 280,92'/><path d='M0,148 C80,170 130,125 165,148 C210,178 240,130 280,148'/><path d='M0,205 C70,178 120,225 160,205 C210,180 240,228 280,205'/><path d='M0,255 C60,280 130,238 170,258 C220,283 250,235 280,255'/><path d='M30,0 C10,70 55,110 32,140 C8,175 50,220 30,280'/><path d='M85,0 C110,60 75,115 88,145 C105,180 70,225 85,280'/><path d='M140,0 C120,70 155,115 142,148 C128,185 165,230 140,280'/><path d='M195,0 C220,65 185,120 198,150 C215,185 180,235 195,280'/><path d='M250,0 C230,70 270,115 252,145 C232,180 268,228 250,280'/></g></svg>");
          background-size:280px 280px; background-repeat:repeat; opacity:0.06;
        }
        .rv-wrap{ width:100%; max-width:460px; margin:auto 0; display:flex; flex-direction:column; align-items:center; position:relative; z-index:1; }
        .rv-logo{ width:clamp(170px,48vw,220px); height:auto; margin-bottom:clamp(28px,5vh,48px); }
        .rv-card{
          width:100%; background:var(--bg-card); border:1px solid var(--line-2);
          padding:clamp(30px,6vw,44px) clamp(22px,5vw,40px) clamp(26px,4vw,36px);
          text-align:center; position:relative;
        }
        .rv-stripe{ position:absolute; top:0; left:0; right:0; height:3px; background:var(--line-2); }
        .rv-stars{ display:inline-flex; gap:5px; color:var(--gold); font-size:22px; line-height:1; margin-bottom:22px; }
        .rv-card h1{
          font-family:'Rajdhani',sans-serif; font-weight:700; font-style:italic; text-transform:uppercase;
          font-size:clamp(30px,8vw,44px); line-height:0.95; letter-spacing:-0.01em; margin:0 0 18px;
        }
        .rv-lead{ font-size:clamp(14px,3.7vw,15px); line-height:1.6; color:var(--fg-2); max-width:42ch; margin:0 auto clamp(26px,4vw,32px); }
        .rv-page a.rv-cta{
          display:flex; align-items:center; justify-content:center; gap:12px;
          width:100%; min-height:58px; padding:18px 22px;
          background:var(--fg); color:#0a0a0a; border:1px solid var(--fg);
          font-family:'JetBrains Mono',monospace; font-size:clamp(12px,3.4vw,13px);
          letter-spacing:0.18em; text-transform:uppercase; font-weight:500;
          cursor:pointer; transition:background .15s,border-color .15s,transform .06s;
        }
        .rv-cta .rv-st{ color:var(--gold); font-size:1.15em; }
        .rv-cta:hover{ background:#fff; border-color:#fff; }
        .rv-cta:active{ transform:translateY(1px); }
        .rv-cta:focus-visible{ outline:2px solid var(--gold); outline-offset:3px; }
        .rv-hint{
          font-family:'JetBrains Mono',monospace; font-size:clamp(9.5px,2.9vw,10.5px);
          letter-spacing:0.12em; text-transform:uppercase; color:var(--fg-3); margin-top:18px; line-height:1.5;
        }
        .rv-alt{ margin-top:clamp(24px,4vw,30px); padding-top:clamp(22px,3.5vw,26px); border-top:1px solid var(--line); }
        .rv-alt-lead{ font-size:clamp(13px,3.5vw,14px); line-height:1.5; color:var(--fg-2); margin:0 0 16px; }
        .rv-alt-btns{ display:flex; gap:12px; }
        .rv-page a.rv-cta2{
          flex:1; display:flex; align-items:center; justify-content:center; gap:8px;
          min-height:48px; padding:12px 16px; background:transparent; color:var(--fg);
          border:1px solid var(--line-2); font-family:'JetBrains Mono',monospace;
          font-size:clamp(11px,3vw,12px); letter-spacing:0.16em; text-transform:uppercase; font-weight:500;
          transition:background .15s,border-color .15s,transform .06s;
        }
        .rv-cta2:hover{ border-color:var(--line-3); background:var(--bg-card-2); }
        .rv-cta2:active{ transform:translateY(1px); }
        .rv-cta2:focus-visible{ outline:2px solid var(--gold); outline-offset:3px; }
        .rv-foot{
          position:relative; z-index:1; font-family:'JetBrains Mono',monospace;
          font-size:clamp(10px,3vw,11px); letter-spacing:0.16em; text-transform:uppercase;
          color:var(--fg-3); text-align:center; padding-top:30px; line-height:1.6;
        }
        .rv-bar{ display:inline-flex; height:3px; width:42px; margin-bottom:14px; background:var(--line-2); }
        /* Espera: puntos titilando */
        .rv-dots{ display:flex; justify-content:center; gap:10px; margin:26px 0; }
        .rv-dots i{
          width:10px; height:10px; background:var(--gold); border-radius:50%;
          animation:rv-blink 1.2s infinite ease-in-out;
        }
        .rv-dots i:nth-child(2){ animation-delay:0.2s; }
        .rv-dots i:nth-child(3){ animation-delay:0.4s; }
        @keyframes rv-blink{ 0%,80%,100%{ opacity:0.15; transform:scale(0.85);} 40%{ opacity:1; transform:scale(1);} }
        .rv-wait-title{
          font-family:'JetBrains Mono',monospace; font-size:clamp(12px,3.4vw,13px);
          letter-spacing:0.18em; text-transform:uppercase; color:var(--fg); font-weight:500;
        }
        /* Reseña confirmada — acento ROJO (diseño de Rodrigo) */
        .rv-page{ --red:#d92038; --red-soft:rgba(217,32,56,0.12); --red-line:rgba(217,32,56,0.55); }
        .rv-stars-white{ color:var(--fg); }
        .rv-coupon{
          border:1px dashed var(--red-line); background:var(--red-soft);
          padding:clamp(22px,5vw,30px) 20px;
          display:flex; flex-direction:column; align-items:center; gap:10px;
          margin-bottom:16px;
        }
        .rv-coupon .rv-code{
          font-family:'JetBrains Mono',monospace; font-weight:700; color:var(--red);
          font-size:clamp(26px,8vw,38px); letter-spacing:0.14em; line-height:1;
        }
        .rv-coupon .rv-meta{
          font-family:'JetBrains Mono',monospace; font-size:clamp(10px,3vw,11px);
          letter-spacing:0.16em; text-transform:uppercase; color:var(--fg-2); line-height:1.5;
        }
        .rv-coupon .rv-meta b{ color:var(--fg); font-weight:600; }
        .rv-page button.rv-dl{
          display:flex; align-items:center; justify-content:center; gap:10px;
          width:100%; min-height:54px; margin-top:clamp(20px,3.5vw,26px);
          padding:16px 22px; background:var(--fg); color:#0a0a0a; border:1px solid var(--fg);
          font-family:'JetBrains Mono',monospace; font-size:clamp(12px,3.4vw,13px);
          letter-spacing:0.18em; text-transform:uppercase; font-weight:500;
          cursor:pointer; transition:background .15s,border-color .15s,transform .06s;
        }
        .rv-page button.rv-dl:hover{ background:#fff; border-color:#fff; }
        .rv-page button.rv-dl:active{ transform:translateY(1px); }
        .rv-page button.rv-dl:focus-visible{ outline:2px solid var(--red); outline-offset:3px; }
        .rv-page button.rv-dl svg{ width:16px; height:16px; }
      `}</style>

      <div className="rv-wf" />

      <div className="rv-wrap">
        <img
          className="rv-logo"
          src={getLogoDarkUrl('/rocam-logo-white.png')}
          onError={(e: any) => { e.currentTarget.src = '/rocam-logo-white.png'; }}
          alt="ROCAM"
        />

        <div className="rv-card">
          <span className="rv-stripe" />

          {phase === 'idle' && (
            <>
              <div className="rv-stars">★★★★★</div>
              <h1>{data.title}</h1>
              <p className="rv-lead">{data.message}</p>
              <a
                className="rv-cta"
                href={data.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackClick}
              >
                {data.button} <span className="rv-st">★</span>
              </a>
              <div className="rv-hint">Se abre tu app de Google Maps — menos de un minuto</div>

              <div className="rv-alt">
                <p className="rv-alt-lead">¿Algo no salió como esperabas? Contáctanos y lo resolvemos.</p>
                <div className="rv-alt-btns">
                  <a className="rv-cta2" href={`https://wa.me/${data.whatsapp}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                  <a className="rv-cta2" href={`tel:${data.phone}`}>Llamar</a>
                </div>
              </div>
            </>
          )}

          {phase === 'waiting' && (
            <>
              <div className="rv-stars">★★★★★</div>
              <h1>¡Gracias!</h1>
              <p className="rv-lead">Estamos confirmando tu reseña con Google…</p>
              <div className="rv-dots"><i /><i /><i /></div>
              <div className="rv-wait-title">Esperando confirmación</div>
              <div className="rv-hint" style={{ marginTop: 14 }}>
                {slowNote
                  ? 'Puede demorar unos minutos. Podés cerrar esta página: tu sorpresa queda guardada a tu nombre.'
                  : 'No cierres esta página — hay una sorpresa esperándote'}
              </div>
              <div className="rv-alt">
                <a
                  className="rv-cta2"
                  style={{ width: '100%' }}
                  href={data.googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Volver a abrir Google
                </a>
              </div>
            </>
          )}

          {phase === 'confirmed' && (
            <>
              <div className="rv-stars rv-stars-white">★★★★★</div>
              <h1>¡Reseña<br />confirmada!</h1>
              <p className="rv-lead">
                Gracias por la reseña. Este es tu código de descuento para la próxima visita. Mostralo en el mostrador.
              </p>
              <div className="rv-coupon">
                <div className="rv-code">{reward.code || 'ROCAM'}</div>
                <div className="rv-meta">
                  {reward.value || ''} de descuento
                  {reward.expiresAt ? <> · válido hasta <b>{new Date(reward.expiresAt).toLocaleDateString('es-AR')}</b></> : null}
                </div>
              </div>
              <div className="rv-hint">Guardá la captura para tenerla a mano</div>
              <button className="rv-dl" type="button" onClick={downloadCoupon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>
                Descargá tu cupón
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rv-foot">
        <span className="rv-bar" /><br />
        {data.business}
      </div>
    </div>
  );
};

export default ReviewRedirect;
